import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import type { MouseEvent } from 'react';
import { memo, useEffect, useMemo, useRef } from 'react';

import { CONTAINER } from '../classes';
import { cls, FORM_ID, intlLocale, m } from '../shared';
import type { McTranslate } from './engine';
import type { GeoData, RouteId } from './geo';
import { fold, GEO_FALLBACK } from './geo';

/** The static markup is the US profile (the design's HTML): what a visitor sees before the engine takes over. */
const INITIAL_ROUTES: Array<{ id: RouteId; code: string; city: string; ms: number; spark: string }> = [
  { id: 'r1', code: 'CHI', city: 'Chicago', ms: 2, spark: '1.5,1.5 8.5,5 15.5,2.3 22.5,1.5 29.5,4.1 36.5,5.9 43.5,2.3 50.5,3.2' },
  { id: 'r2', code: 'DTW', city: 'Detroit', ms: 4, spark: '1.5,10.3 8.5,13 15.5,9.4 22.5,11.2 29.5,12.1 36.5,8.5 43.5,10.3 50.5,11.2' },
  { id: 'r3', code: 'STL', city: 'St. Louis', ms: 15, spark: '1.5,11.9 8.5,12.8 15.5,11 22.5,12.8 29.5,11.9 36.5,13.7 43.5,12.8 50.5,13.7' },
  { id: 'r4', code: 'MSP', city: 'Minneapolis', ms: 21, spark: '1.5,5.2 8.5,7.9 15.5,4.3 22.5,7 29.5,5.2 36.5,6.1 43.5,8.8 50.5,6.1' },
  { id: 'r5', code: 'MCI', city: 'Kansas City', ms: 23, spark: '1.5,8.8 8.5,6.1 15.5,9.7 22.5,7 29.5,7.9 36.5,9.7 43.5,6.1 50.5,7.9' },
];
const INITIAL_BAD = 208;

/** English exonyms for the pt city names geo-data.json carries (its aliases map English spellings to pt). */
function englishNames(geoData: GeoData): Record<string, string> {
  const names: Record<string, string> = {};
  Object.entries(geoData.aliases ?? {}).forEach(([alias, ptName]) => {
    // an alias that only drops the accents ("sao paulo") is no exonym: the pt spelling stays
    if (names[ptName] || fold(alias) === fold(ptName)) return;
    names[ptName] = alias.replace(/\b\w/g, (letter) => letter.toUpperCase());
  });
  return names;
}

function RouteButton({ route, index, country, t }: { route: (typeof INITIAL_ROUTES)[number]; index: number; country: string; t: McTranslate }) {
  const isWinner = index === 0;
  const lastPoint = route.spark.split(' ').pop()?.split(',') ?? ['0', '0'];
  return (
    <li>
      <button className={m(isWinner ? 'mc-route is-winner' : 'mc-route')} type='button' data-route={route.id} aria-pressed='false' aria-label={isWinner ? t('routeWinner', { city: route.city, country, ms: route.ms }) : t('routeSlower', { city: route.city, country, ms: route.ms, gap: route.ms - 2 })}>
        <span className={m('mc-route-cc')} aria-hidden='true' data-route-cc=''>
          {route.code}
        </span>
        <span className={m('mc-route-body')}>
          <span className={m('mc-route-city')} data-route-city=''>
            {route.city}
          </span>
          <span className={m('mc-route-meta')}>
            <span className={m('mc-route-country')} data-route-country=''>
              {country}
            </span>
            <span className={m('mc-route-tag')}>{t('fastest')}</span>
            <span className={m('mc-route-pin')}>
              <svg viewBox='0 0 16 16' aria-hidden='true'>
                <path d='M9.6 1.8 14.2 6.4l-2 .5-1.8 1.8.4 3.4-1.3 1.3-2.9-2.9-4.2 4.2m3-5.5L2.6 6.4l1.3-1.3 3.4.4 1.8-1.8z' />
              </svg>
              {t('pinned')}
            </span>
          </span>
        </span>
        <svg className={m('mc-route-spark')} viewBox='0 0 52 18' aria-hidden='true' focusable='false' data-spark=''>
          <polygon className={m('sp-fill')} points={`1.5,18 ${route.spark} 50.5,18`} />
          <polyline points={route.spark} />
          <circle className={m('sp-dot')} r='1.75' cx={lastPoint[0]} cy={lastPoint[1]} />
        </svg>
        <span className={m('mc-route-lat')}>
          <span className={m('mc-route-ms')} data-ms-wrap=''>
            <span data-ms=''>{route.ms}</span>
            <span className={m('u')}>{t('unit')}</span>
          </span>
          <span className={m('mc-route-delta')} data-delta=''>
            {isWinner ? t('activeNow') : t('plusMs', { ms: route.ms - 2 })}
          </span>
        </span>
      </button>
    </li>
  );
}

function MulticonnectionCard({ onTrialClick }: { onTrialClick: (event: MouseEvent<HTMLAnchorElement>) => void }) {
  const { t: translate } = useTranslation('pages/trial-matrix');
  const { locale } = useRouter();
  const rootRef = useRef<HTMLDivElement>(null);
  const t = useMemo<McTranslate>(() => (key, vars) => translate(`lp.mc.${key}`, { ...vars, interpolation: { escapeValue: false } }), [translate]);
  const onTrialRef = useRef(onTrialClick);
  onTrialRef.current = onTrialClick;

  const intl = intlLocale(locale);
  const countryName = useMemo(() => {
    let display: Intl.DisplayNames | null = null;
    try {
      display = typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames([intl], { type: 'region' }) : null;
    } catch {
      display = null;
    }
    return (cc: string, fallback: string) => (locale === 'pt' ? fallback : (display?.of(cc) ?? fallback));
  }, [intl, locale]);
  const usCountry = countryName('US', 'Estados Unidos');

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    let destroy: (() => void) | null = null;
    let cancelled = false;
    const boot = () => {
      Promise.all([import('./geo-data.json').then((module) => module.default as unknown as GeoData).catch(() => GEO_FALLBACK), import('./engine')])
        .then(([geoData, engine]) => {
          if (cancelled) return;
          const data = geoData?.countries?.US ? geoData : GEO_FALLBACK;
          const exonyms = locale === 'pt' ? {} : englishNames(data);
          const listFormat = typeof Intl.ListFormat === 'function' ? new Intl.ListFormat(intl, { style: 'long', type: 'conjunction' }) : null;
          destroy = engine.createMcEngine({
            root,
            geoData: data,
            cls,
            t,
            countryName,
            cityName: (name) => exonyms[name] ?? name,
            listJoin: (items) => (listFormat ? listFormat.format(items) : items.join(', ')),
          });
        })
        .catch(() => undefined);
    };
    // the data (~34 KB) is only fetched when the card comes near the screen: never on the first load
    let observer: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            observer?.disconnect();
            boot();
          }
        },
        { rootMargin: '600px 0px' }
      );
      observer.observe(root);
    } else boot();
    return () => {
      cancelled = true;
      observer?.disconnect();
      destroy?.();
    };
  }, [t, countryName, locale, intl]);

  const ms = t('unit');

  return (
    <div className={m('mc')} ref={rootRef}>
      <div className={m('mc-card')}>
        <header className={m('mc-head')}>
          <span className={m('eyebrow')}>{t('eyebrow')}</span>
          <h2 className={m('mc-title')} id='mc-title'>
            <span className={m('mc-title-a')}>
              {t('titleA')} <span className={m('mc-title-b')}>{t('titleB')}</span>
            </span>{' '}
            <em>{t('titleEm')}</em>
          </h2>
          <p className={m('mc-lead')}>
            <Trans t={translate} i18nKey='lp.mc.lead' components={{ b: <b /> }} />
          </p>
          <ul className={m('mc-facts')}>
            <li>
              <svg viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M14 3 5 13.5h6.5L10 21l9-10.5h-6.5z' />
              </svg>
              <span>
                <Trans t={translate} i18nKey='lp.mc.factSwitch' components={{ b: <b /> }} />
              </span>
            </li>
            <li>
              <svg viewBox='0 0 24 24' aria-hidden='true'>
                <circle cx='12' cy='12' r='9' />
                <path d='M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18' />
              </svg>
              <span>
                <Trans t={translate} i18nKey='lp.mc.factServers' components={{ b: <b /> }} />
              </span>
            </li>
          </ul>
        </header>

        <div className={m('mc-proof')} role='group' aria-label={t('proofLabel')} data-proof=''>
          <p className='sr-only' data-proof-sr=''>
            {t('proofSr', { bad: INITIAL_BAD, good: 2, pct: 99 })}
          </p>
          <div className={m('mc-proof-rows')} aria-hidden='true'>
            <div className={m('mc-proof-item mc-proof-item--bad')}>
              <span className={m('mc-proof-line')}>
                <small>{t('without')}</small>
                <strong>
                  <span data-proof-bad=''>{INITIAL_BAD}</span>
                  <span className={m('u')}>{ms}</span>
                </strong>
              </span>
              <span className={m('mc-proof-bar')}>
                <span className={m('mc-proof-fill')} />
              </span>
            </div>
            <div className={m('mc-proof-item mc-proof-item--good')}>
              <span className={m('mc-proof-line')}>
                <small>{t('with')}</small>
                <span className={m('mc-proof-pct')}>
                  −<span data-proof-pct=''>99</span>%
                </span>
                <strong>
                  <span data-proof-good=''>2</span>
                  <span className={m('u')}>{ms}</span>
                </strong>
              </span>
              <span className={m('mc-proof-bar')}>
                <span className={m('mc-proof-fill')} data-proof-fill='' />
              </span>
            </div>
          </div>
          <p className={m('mc-proof-note')}>
            <Trans t={translate} i18nKey='lp.mc.proofNote' components={{ b: <b /> }} />
          </p>
        </div>

        <p className='sr-only' id='mc-desc' data-desc=''>
          {t('desc', { cities: 'Chicago, Detroit, St. Louis, Minneapolis, Kansas City', hub: 'Chicago' })}
        </p>

        <div className={m('mc-stage')} data-stage='' role='group' aria-label={t('stageLabel')} aria-describedby='mc-desc'>
          <svg className={m('mc-defs')} aria-hidden='true' focusable='false'>
            <defs>
              <linearGradient id='mc-spg' x1='0' y1='0' x2='0' y2='1'>
                <stop className={m('g0')} offset='0' />
                <stop className={m('g1')} offset='1' />
              </linearGradient>
            </defs>
          </svg>
          <svg className={m('mc-wires')} data-wires='' aria-hidden='true' focusable='false' />
          <div className={m('mc-packets')} data-packets='' aria-hidden='true' />

          <div className={m('mc-node mc-node--pc')} data-node='pc'>
            <div className={m('mc-node-icon')} aria-hidden='true' data-node-icon=''>
              <svg viewBox='0 0 40 40'>
                <rect x='5' y='7' width='30' height='20' rx='3' />
                <path d='M14 34h12M20 27v7' />
                <circle cx='20' cy='17' r='5' />
                <path d='M20 9v3.5M20 21.5V25M12 17h3.5M24.5 17H28' />
                <circle className={m('f')} cx='20' cy='17' r='1.4' />
              </svg>
            </div>
            <div className={m('mc-node-label')}>
              <strong>{t('pc')}</strong>
              <small data-pc-place=''>{t('pcPlace')}</small>
            </div>
          </div>

          <div className={m('mc-routes-wrap')} data-routes-wrap=''>
            <div className={m('mc-key')}>
              <span className={m('mc-key-col')} aria-hidden='true'>
                <span className={m('x-long')}>
                  <Trans t={translate} i18nKey='lp.mc.keyLong' components={{ b: <b /> }} />
                </span>
                <span className={m('x-short')}>
                  <Trans t={translate} i18nKey='lp.mc.keyShort' components={{ b: <b /> }} />
                </span>
              </span>
              <p className={m('mc-hint')} data-hint='' aria-hidden='true'>
                <svg className={m('x-touch')} viewBox='0 0 24 24'>
                  <circle className={m('dot')} cx='15' cy='15' r='2.4' />
                  <path d='M9.17 17.12A6.2 6.2 0 0 1 17.12 9.17M5.04 18.63A10.6 10.6 0 0 1 18.63 5.04' />
                </svg>
                <svg className={m('x-hover')} viewBox='0 0 24 24'>
                  <path d='M5 3l14 8.2-6.4 1.6L9.4 19z' />
                </svg>
                <span className={m('x-touch')}>{t('hintTouch')}</span>
                <span className={m('x-hover')}>{t('hintHover')}</span>
              </p>
              <button className={m('mc-clear')} type='button' data-clear='' hidden title={t('clearTitle')} aria-label={t('clearAria')}>
                <svg viewBox='0 0 12 12' aria-hidden='true'>
                  <path d='M2.5 2.5l7 7M9.5 2.5l-7 7' />
                </svg>
                <span className={m('x-long')}>{t('clearLong')}</span>
                <span className={m('x-short')}>{t('clearShort')}</span>
              </button>
            </div>
            <ul className={m('mc-routes')} data-routes='' aria-label={t('routesLabel')}>
              {INITIAL_ROUTES.map((route, index) => (
                <RouteButton key={route.id} route={route} index={index} country={usCountry} t={t} />
              ))}
            </ul>
            <p className={m('mc-direct-key')} aria-hidden='true'>
              {t('directKey')}
            </p>
            <div className={m('mc-direct')} data-direct='' role='group' aria-label={t('directAria', { gap: INITIAL_BAD - 2 })}>
              <i aria-hidden='true' />
              <span>
                <b>{t('directName')}</b>
                <span className={m('x-d')}>
                  <span className={m('x-sep')}> </span>
                  {t('directSub')}
                </span>
              </span>
              <strong>
                +<span data-direct-ms=''>{INITIAL_BAD - 2}</span>
                <span className={m('u')}>{ms}</span>
              </strong>
            </div>
          </div>

          <div className={m('mc-node mc-node--game')} data-node='game'>
            <div className={m('mc-node-icon')} aria-hidden='true' data-node-icon=''>
              <svg viewBox='0 0 40 40'>
                <rect x='7' y='7' width='26' height='10' rx='2.5' />
                <rect x='7' y='23' width='26' height='10' rx='2.5' />
                <path d='M12 12h6M12 28h6' />
                <circle className={m('f')} cx='27' cy='12' r='1.6' />
                <circle className={m('f')} cx='27' cy='28' r='1.6' />
              </svg>
            </div>
            <div className={m('mc-node-label')}>
              <strong>{t('game')}</strong>
              <small data-game-place=''>{t('gamePlace', { city: 'Chicago' })}</small>
            </div>
          </div>

          <div className={m('mc-result')} aria-hidden='true'>
            <span className={m('mc-result-side mc-result-side--bad')}>
              <small>{t('without')}</small>
              <b>
                <span data-result-bad=''>{INITIAL_BAD}</span>
                <i>{ms}</i>
              </b>
            </span>
            <span className={m('mc-result-line')}>
              <svg viewBox='0 0 12 12'>
                <path d='M3.5 1.5L8 6l-4.5 4.5' />
              </svg>
            </span>
            <span className={m('mc-result-side mc-result-side--good')}>
              <small>{t('with')}</small>
              <span className={m('mc-result-fig')}>
                <span className={m('mc-proof-pct')}>
                  −<span data-result-pct=''>99</span>%
                </span>
                <b>
                  <span data-result-good=''>2</span>
                  <i>{ms}</i>
                </b>
              </span>
            </span>
          </div>
        </div>

        <footer className={m('mc-foot')}>
          <div className={m('mc-foot-text')}>
            <p className={m('mc-status')}>
              <i aria-hidden='true' />
              {/* engine-owned: replaced wholesale on every hand-over, so React only ever sets it as opaque HTML
                  (our own translation plus numbers; city names are escaped by the engine) */}
              {/* eslint-disable-next-line react/no-danger */}
              <span className={m('mc-status-body')} data-status-text='' dangerouslySetInnerHTML={{ __html: t('statusFront', { city: 'Chicago', ms: '<span data-st-ms="r1">2</span>' }) }} />
            </p>
            <div className={m('mc-foot-slot')}>
              <p className={m('mc-bridge')} data-bridge=''>
                {t('bridge')}
              </p>
            </div>
          </div>
          <div className={m('mc-actions')}>
            <button className={m('mc-pause')} type='button' data-pause='' aria-label={t('pauseAria')} aria-pressed='false' title={t('pauseTitle')}>
              <svg className={m('ic-pause')} viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M8.5 5.5v13M15.5 5.5v13' />
              </svg>
              <svg className={m('ic-play')} viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
              </svg>
            </button>
            <button className={m('btn btn-secondary mc-btn')} type='button' data-retest='' aria-busy='false'>
              <svg viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7' />
              </svg>
              <span className={m('mc-btn-lbl')}>
                <span data-lbl=''>{t('retest')}</span>
                <span className={m('mc-btn-ghost')} aria-hidden='true'>
                  {t('retest')}
                </span>
                <span className={m('mc-btn-ghost')} aria-hidden='true'>
                  {t('measuring')}
                </span>
              </span>
            </button>
            <div className={m('mc-offer')}>
              <a className={m('btn btn-primary mc-cta')} href={`#${FORM_ID}`} onClick={(event) => onTrialRef.current(event)}>
                <span>{translate('lp.cta')}</span>
                <svg viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M5 12h13M13 6l6 6-6 6' />
                </svg>
              </a>
              <p className={m('mc-offer-note')}>
                <Trans t={translate} i18nKey='lp.mc.offerNote' components={{ b: <b /> }} />
              </p>
            </div>
          </div>
          <p className='sr-only' role='status' aria-live='polite' data-live='' />
        </footer>
      </div>
    </div>
  );
}

const MemoCard = memo(MulticonnectionCard);

/** Multiconexão section: the native card (was an iframe) inside its size container (`.mc-vp`). */
export function Multiconnection({ onTrialClick }: { onTrialClick: (event: MouseEvent<HTMLAnchorElement>) => void }) {
  const { i18n } = useTranslation('pages/trial-matrix');
  const onTrialRef = useRef(onTrialClick);
  onTrialRef.current = onTrialClick;
  const stableOnTrial = useMemo(() => (event: MouseEvent<HTMLAnchorElement>) => onTrialRef.current(event), []);

  return (
    <section className='relative scroll-mt-6 py-[clamp(2rem,1rem+3vw,4rem)]' id='tecnologia' aria-labelledby='mc-title'>
      <div className={CONTAINER}>
        <div className={m('mc-vp')}>
          {/* keyed by language: a locale switch rebuilds the card and its engine from scratch */}
          <MemoCard key={i18n.language} onTrialClick={stableOnTrial} />
        </div>
      </div>
    </section>
  );
}
